import moment from 'moment'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'

export interface CharterRecommendationDates {
  departureDate: string
  returnDate?: string
  nights?: number
}

const CHARTER_SUGGESTION_WINDOW_DAYS = 14

export function charterDayDistance(first: string, second: string): number {
  return Math.abs(moment.utc(first).diff(moment.utc(second), 'days'))
}

/** Search nearby operating months first; preserve travel length before trying bundle stay lengths. */
export function getCharterFallbackDateGroups<T extends CharterRecommendationDates>(dates: Array<T>, departureDate: string, returnDate?: string): Array<{ matchingDuration: Array<T>, bundleDuration: Array<T> }> {
  const months = new Map<number, Array<T>>()
  const requestedDuration = returnDate ? charterDayDistance(returnDate, departureDate) : undefined
  for (const candidate of dates) {
    const distance = Math.abs(moment.utc(candidate.departureDate).startOf('month').diff(moment.utc(departureDate).startOf('month'), 'months'))
    const group = months.get(distance) ?? []
    group.push(candidate)
    months.set(distance, group)
  }
  return Array.from(months.entries()).toSorted(([first], [second]) => first - second).map(([, candidates]) => ({
    matchingDuration: candidates.filter(candidate => !returnDate || (!!candidate.returnDate && charterDayDistance(candidate.returnDate, candidate.departureDate) === requestedDuration)),
    bundleDuration: candidates.filter(candidate => !!returnDate && candidate.nights !== undefined &&
      CHARTER_FLIGHT_DETAILS['melbourne-maldives'].validDurations.includes(candidate.nights) &&
      (!candidate.returnDate || charterDayDistance(candidate.returnDate, candidate.departureDate) !== requestedDuration)),
  }))
}

export function isCharterSearchWithinWindow(candidate: CharterRecommendationDates, departureDate: string, returnDate?: string): boolean {
  return charterDayDistance(candidate.departureDate, departureDate) <= CHARTER_SUGGESTION_WINDOW_DAYS &&
    (!returnDate || (!!candidate.returnDate && charterDayDistance(candidate.returnDate, returnDate) <= CHARTER_SUGGESTION_WINDOW_DAYS))
}

export function rankCharterRecommendations<T extends CharterRecommendationDates & { totalPrice: number }>(candidates: Array<T>, departureDate?: string, returnDate?: string): Array<T> {
  const proximity = (candidate: T) => departureDate ? charterDayDistance(candidate.departureDate, departureDate) +
    (returnDate && candidate.returnDate ? charterDayDistance(candidate.returnDate, returnDate) : 0) : 0
  return candidates.toSorted((first, second) => first.totalPrice - second.totalPrice || proximity(first) - proximity(second) ||
    first.departureDate.localeCompare(second.departureDate) || (first.returnDate ?? '').localeCompare(second.returnDate ?? ''))
}
